import { Directive, computed, input } from '@angular/core';
import { Tag } from 'primeng/tag';

export type EnumTagSeverity =
  | 'secondary'
  | 'info'
  | 'warn'
  | 'success'
  | 'danger'
  | 'contrast';

/** Imports shared by every concrete enum-tag component. */
export const ENUM_TAG_IMPORTS = [Tag];

/** Template shared by every concrete enum-tag component. */
export const ENUM_TAG_TEMPLATE = `
  @if (value() !== null && value() !== undefined) {
    <p-tag
      [value]="label()"
      [severity]="severity()"
      [icon]="icon()"
      [rounded]="true"
      styleClass="whitespace-nowrap"
    />
  } @else {
    <span class="text-muted-color">--</span>
  }
`;

/**
 * READ-ONLY display badge for an enum value — the single, uniform way to show an
 * enum (mobility, status, orientation, …) as a colored tag.
 *
 * ─── The rule (read this before reusing) ────────────────────────────────────
 *  • OUTSIDE a form (tables, detail views, cards, read-only summaries): use an
 *    enum-tag component and bind the value with `[value]="…"`.
 *  • INSIDE a form (editable): DO NOT use an enum-tag. Use the default
 *    select-button pattern — `app-form-select-button-wrapper` + `p-select-button`
 *    with options built from the enum — exactly like every other form. In read
 *    mode the form disables that select-button; it never turns into a tag.
 *
 * These components are intentionally NOT `ControlValueAccessor`s: an enum-tag is
 * a presentation-only badge, never a form control. Keeping the two concerns apart
 * is what prevents the old "same component is a tag here and an input there"
 * confusion — don't add form-binding back to it.
 *
 * Subclasses implement {@link translate} and may override {@link severityFor} /
 * {@link iconFor} to colorize.
 *
 * @template T the enum string type.
 */
@Directive()
export abstract class AbstractEnumTag<T extends string> {
  /** The enum value to display; `null`/`undefined` renders an em-dash. */
  readonly value = input<T | null | undefined>(null);

  /** Translates an enum value to its display label. */
  protected abstract translate(value: T): string;

  /** Tag severity for a value. Override to colorize. */
  protected severityFor(_value: T): EnumTagSeverity {
    return 'secondary';
  }

  /** Tag icon (PrimeIcons class) for a value. Override to add icons. */
  protected iconFor(_value: T): string | undefined {
    return undefined;
  }

  protected readonly label = computed(() => {
    const value = this.value();
    return value ? this.translate(value) : '';
  });

  protected readonly severity = computed<EnumTagSeverity>(() => {
    const value = this.value();
    return value ? this.severityFor(value) : 'secondary';
  });

  protected readonly icon = computed(() => {
    const value = this.value();
    return value ? this.iconFor(value) : undefined;
  });
}
